/**
 * Password policy resolution + validation (R5 / W6-4 / D5).
 *
 * Org admins configure a password policy in Settings → Security
 * (minPasswordLength + complexity toggles). Those keys were previously stored
 * but never enforced — every form hardcoded a length floor and ignored
 * complexity. This module is the single source of truth that turns the stored
 * policy into real validation.
 *
 * Pure module (no React / no Supabase) so it is trivially unit-testable and
 * usable from any surface.
 */

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

/** Clamp bounds for a configured minimum length (mirrors the Settings UI input). */
export const MIN_PASSWORD_LENGTH_FLOOR = 6;
export const MIN_PASSWORD_LENGTH_CEIL = 32;

/**
 * Baseline applied when an org has NOT configured a policy (or there is no org
 * context — e.g. a pre-auth recovery/invite flow). Intentionally permissive so
 * this change enforces what admins explicitly set without retroactively imposing
 * complexity on orgs that never opted in. It is still ≥ the previous hardcoded
 * floor and never weaker than 8 characters.
 */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  requireUppercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
};

/** Subset of org settings this module reads. */
export interface PasswordPolicySource {
  minPasswordLength?: number | null;
  requireUppercase?: boolean | null;
  requireNumbers?: boolean | null;
  requireSpecialChars?: boolean | null;
}

/**
 * Resolve the effective policy from stored org settings, falling back to the
 * permissive baseline for any unset key. The configured minimum length is
 * clamped to the same [6, 32] range the Settings UI enforces.
 */
export function resolvePasswordPolicy(
  settings: PasswordPolicySource | null | undefined,
): PasswordPolicy {
  const rawMin = settings?.minPasswordLength;
  const minLength =
    typeof rawMin === "number" && Number.isFinite(rawMin)
      ? Math.min(MIN_PASSWORD_LENGTH_CEIL, Math.max(MIN_PASSWORD_LENGTH_FLOOR, Math.round(rawMin)))
      : DEFAULT_PASSWORD_POLICY.minLength;

  return {
    minLength,
    requireUppercase: settings?.requireUppercase ?? DEFAULT_PASSWORD_POLICY.requireUppercase,
    requireNumbers: settings?.requireNumbers ?? DEFAULT_PASSWORD_POLICY.requireNumbers,
    requireSpecialChars:
      settings?.requireSpecialChars ?? DEFAULT_PASSWORD_POLICY.requireSpecialChars,
  };
}

export function resolveStrictestPasswordPolicy(
  sources: Array<PasswordPolicySource | null | undefined>,
): PasswordPolicy {
  const [first, ...rest] = sources.map((source) => resolvePasswordPolicy(source));
  if (!first) return DEFAULT_PASSWORD_POLICY;

  return rest.reduce<PasswordPolicy>((strictest, next) => {
    return {
      minLength: Math.max(strictest.minLength, next.minLength),
      requireUppercase: strictest.requireUppercase || next.requireUppercase,
      requireNumbers: strictest.requireNumbers || next.requireNumbers,
      requireSpecialChars: strictest.requireSpecialChars || next.requireSpecialChars,
    };
  }, first);
}

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/** Validate a candidate password against a resolved policy. */
export function validatePassword(
  password: string,
  policy: PasswordPolicy,
): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < policy.minLength) {
    errors.push(`Password must be at least ${policy.minLength} characters`);
  }
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push("Password must include an uppercase letter");
  }
  if (policy.requireNumbers && !/[0-9]/.test(password)) {
    errors.push("Password must include a number");
  }
  if (policy.requireSpecialChars && !/[^A-Za-z0-9]/.test(password)) {
    errors.push("Password must include a special character");
  }

  return { valid: errors.length === 0, errors };
}

/** Human-readable one-line description of the requirements (for hints/placeholders). */
export function describePasswordPolicy(policy: PasswordPolicy): string {
  const parts: string[] = [`at least ${policy.minLength} characters`];
  if (policy.requireUppercase) parts.push("an uppercase letter");
  if (policy.requireNumbers) parts.push("a number");
  if (policy.requireSpecialChars) parts.push("a special character");

  if (parts.length === 1) return `Use ${parts[0]}.`;
  const last = parts.pop();
  return `Use ${parts.join(", ")} and ${last}.`;
}
